import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PaymentDetailsCard — "How do we pay this person?"
 *
 * Sits in the admin staff-profile drawer. Renders conditional sections
 * based on `employment_type`:
 *   - employee   → bank, address, NI, tax code, pension
 *   - contractor → bank, address, UTR, VAT, invoice email, terms
 *   - volunteer  → only bank (for expense reimbursements)
 *
 * Notes on PII (mirrors the backend comment):
 *   - Bank sort code / account number are stored plain in MongoDB with
 *     admin-only RBAC on the API. Field-level encryption is on the
 *     follow-up backlog.
 *   - For non-admin viewers the API never returns these fields at all,
 *     so this component is only ever rendered for admin roles.
 */
const PAY_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'fortnightly', label: 'Fortnightly (every 2 weeks)' },
  { value: 'weekly', label: 'Weekly' },
];

const EMPLOYMENT_LABEL = {
  employee: 'Employee (PAYE)',
  contractor: 'Contractor (self-invoicing)',
  volunteer: 'Volunteer (unpaid)',
};

export function PaymentDetailsCard({ staffId, staffName, employmentType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!staffId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await api.getStaffPaymentDetails(staffId);
        if (!cancelled) {
          setData(d || {});
          setDirty(false);
        }
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Could not load payment details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [staffId]);

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await api.updateStaffPaymentDetails(staffId, data);
      setData(fresh);
      setDirty(false);
      toast.success('Payment details saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginTop: '1rem' }} data-testid="payment-details-loading">
        <div style={{ color: '#6b7280' }}>Loading payment details…</div>
      </div>
    );
  }

  const isContractor = employmentType === 'contractor';
  const isVolunteer = employmentType === 'volunteer';
  const isEmployee = !isContractor && !isVolunteer;

  return (
    <div className="card" style={{ marginTop: '1rem' }} data-testid="payment-details-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>💷 Payment &amp; HMRC details</h3>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            {staffName ? `For ${staffName} — ` : ''}{EMPLOYMENT_LABEL[employmentType] || employmentType || 'Employee'}
          </p>
        </div>
        <span style={pillStyle('#f3f4f6', '#374151')}>🔒 Admin only</span>
      </div>

      <Section title="Pay basics">
        <Row label="Pay frequency">
          <select
            value={data.pay_frequency || 'monthly'}
            onChange={(e) => set('pay_frequency', e.target.value)}
            data-testid="pay-frequency"
            style={inputStyle}
          >
            {PAY_FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Row>
        <Row label="Hourly rate (£)">
          <input
            type="number" step="0.01"
            value={data.hourly_rate ?? ''}
            onChange={(e) => set('hourly_rate', parseFloat(e.target.value) || 0)}
            data-testid="hourly-rate"
            style={inputStyle}
            disabled={isVolunteer}
            placeholder={isVolunteer ? 'Volunteer — unpaid' : '0.00'}
          />
        </Row>
        <Row label="Contract hours / week">
          <input
            type="number"
            value={data.contract_hours ?? ''}
            onChange={(e) => set('contract_hours', parseInt(e.target.value, 10) || 0)}
            data-testid="contract-hours"
            style={inputStyle}
          />
        </Row>
      </Section>

      <Section title="Bank details (for payments)">
        <Row label="Account holder">
          <input
            type="text"
            value={data.bank_account_holder || ''}
            onChange={(e) => set('bank_account_holder', e.target.value)}
            data-testid="bank-holder"
            style={inputStyle}
          />
        </Row>
        <Row label="Sort code">
          <input
            type="text" placeholder="12-34-56"
            value={data.bank_sort_code || ''}
            onChange={(e) => set('bank_sort_code', e.target.value)}
            data-testid="bank-sort-code"
            style={inputStyle}
          />
        </Row>
        <Row label="Account number">
          <input
            type="text" placeholder="12345678"
            value={data.bank_account_number || ''}
            onChange={(e) => set('bank_account_number', e.target.value)}
            data-testid="bank-account-number"
            style={inputStyle}
          />
        </Row>
      </Section>

      <Section title="Postal address">
        <Row label="Line 1">
          <input type="text" value={data.address_line_1 || ''} onChange={(e) => set('address_line_1', e.target.value)} style={inputStyle} />
        </Row>
        <Row label="Line 2">
          <input type="text" value={data.address_line_2 || ''} onChange={(e) => set('address_line_2', e.target.value)} style={inputStyle} />
        </Row>
        <Row label="City">
          <input type="text" value={data.city || ''} onChange={(e) => set('city', e.target.value)} style={inputStyle} />
        </Row>
        <Row label="Postcode">
          <input type="text" value={data.postcode || ''} onChange={(e) => set('postcode', e.target.value)} style={inputStyle} />
        </Row>
      </Section>

      {isEmployee && (
        <Section title="HMRC / employee">
          <Row label="NI number">
            <input type="text" placeholder="QQ123456C" value={data.ni_number || ''} onChange={(e) => set('ni_number', e.target.value.toUpperCase())} data-testid="ni-number" style={inputStyle} />
          </Row>
          <Row label="Tax code">
            <input type="text" placeholder="1257L" value={data.tax_code || ''} onChange={(e) => set('tax_code', e.target.value.toUpperCase())} data-testid="tax-code" style={inputStyle} />
          </Row>
          <Row label="Pension opt-in">
            <input type="checkbox" checked={!!data.pension_opt_in} onChange={(e) => set('pension_opt_in', e.target.checked)} data-testid="pension-opt-in" />
          </Row>
          {data.pension_opt_in && (
            <>
              <Row label="Pension scheme">
                <input type="text" value={data.pension_scheme || ''} onChange={(e) => set('pension_scheme', e.target.value)} style={inputStyle} />
              </Row>
              <Row label="Pension contribution (%)">
                <input type="number" step="0.1" value={data.pension_contribution_pct ?? ''} onChange={(e) => set('pension_contribution_pct', parseFloat(e.target.value) || 0)} style={inputStyle} />
              </Row>
            </>
          )}
        </Section>
      )}

      {isContractor && (
        <Section title="Contractor invoicing">
          <Row label="UTR (Self-Assessment)">
            <input type="text" value={data.utr || ''} onChange={(e) => set('utr', e.target.value)} data-testid="utr" style={inputStyle} />
          </Row>
          <Row label="VAT number (optional)">
            <input type="text" value={data.vat_number || ''} onChange={(e) => set('vat_number', e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Invoice email">
            <input type="email" value={data.invoice_email || ''} onChange={(e) => set('invoice_email', e.target.value)} data-testid="invoice-email" style={inputStyle} />
          </Row>
          <Row label="Payment terms (days)">
            <input type="number" value={data.payment_terms_days ?? 30} onChange={(e) => set('payment_terms_days', parseInt(e.target.value, 10) || 30)} style={inputStyle} />
          </Row>
        </Section>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="payment-details-save"
          style={{
            padding: '0.6rem 1rem',
            background: !dirty || saving ? '#9ca3af' : '#00BCD4',
            color: '#fff', border: 'none', borderRadius: '6px',
            cursor: !dirty || saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving…' : '💾 Save payment details'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.95rem' }}>{title}</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 0.75rem' }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem', color: '#6b7280', gap: '0.2rem' }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  padding: '0.45rem 0.6rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.9rem',
};

const pillStyle = (bg, fg) => ({
  background: bg, color: fg,
  padding: '0.2rem 0.6rem', borderRadius: '999px',
  fontSize: '0.7rem', fontWeight: 600,
});

export default PaymentDetailsCard;
